<template>
  <div
    class="select-none flex flex-col bg-gray-900 rounded-lg cursor-pointer relative group transition-all duration-300 hover:scale-[102%] hover:z-30 border border-gray-800"
    :data-mod-id="mod.id"
    @click="selectMod(mod)"
    :class="{
      'border-primary-600 border-2 rounded-lg shadow-[0_20px_30px_-15px_rgba(78,84,200,0.5)]': isCardSelected,
      'z-30 rounded-t-lg rounded-b-none': shouldShowTooltip,
    }"
    :style="{ overflow: shouldShowTooltip ? 'visible' : 'hidden' }"
    @mouseenter="showTooltip = true"
    @mouseleave="showTooltip = false"
  >
    <span
      v-if="isCardSelected"
      class="text-white text-sm font-llight rounded bg bg-primary-600 absolute top-3 left-3 px-1 z-20"
    >
      {{ $t('play.play_now.selected') }}
    </span>

    <!-- Favorite Heart Button -->
    <button
      @click.stop="toggleFavorite"
      class="absolute top-2 right-2 z-20 p-1 rounded-full bg-gray-800/80 hover:bg-gray-700/80 transition-colors duration-200"
    >
      <svg
        v-if="isFavorite"
        class="w-4 h-4 text-[#C44CB6]"
        fill="currentColor"
        viewBox="0 0 20 20"
      >
        <path
          fill-rule="evenodd"
          d="M3.172 5.172a4 4 0 015.656 0L10 6.343l1.172-1.171a4 4 0 115.656 5.656L10 17.657l-6.828-6.829a4 4 0 010-5.656z"
          clip-rule="evenodd"
        />
      </svg>
      <svg
        v-else
        class="w-4 h-4 text-gray-400 hover:text-[#C44CB6]"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="2"
          d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z"
        />
      </svg>
    </button>

    <!-- Image container with fixed aspect ratio -->
    <div class="relative w-full aspect-video bg-gray-800 rounded-t-lg">
      <img
        :src="imageUrl"
        :alt="mod.name"
        class="w-full h-full object-cover rounded-t-lg bg-gray-800 select-none pointer-events-none user-select-none drag-none"
      />
    </div>

    <div class="flex flex-col gap-2 p-4 bg-gray-900 items-start">
      <h2 class="text-sm font-medium text-text-primary">{{ mod.friendly_name }}</h2>
      <div class="flex flex-row gap-2">
        <div
          v-if="showCategory && category"
          class="text-text-secondary text-xs font-light flex gap-1 items-center px-2 py-1 rounded-md"
          :style="{ backgroundColor: `${category.color}15` }"
        >
          <img :src="`/img/icons/${category.name.toLowerCase()}.svg`" class="w-3 h-3" />
          <p>{{ category.name }}</p>
        </div>

        <div
          class="text-text-secondary text-xs font-light flex gap-1 items-center px-2 py-1 rounded-md bg-gray-800"
        >
          <img v-if="mod.player_count == 1" src="/img/icons/solo.svg" class="w-3 h-3" />
          <img v-else src="/img/icons/duo.svg" class="w-3 h-3" />
          <span
            class="text-text-secondary text-xs font-light"
          >
            {{ mod.player_count_text }}
          </span>
        </div>

        <div
          v-if="mod?.children?.length > 0"
          class="text-text-secondary text-xs font-light flex gap-1 items-center px-2 py-1 rounded-md bg-gray-800"
        >
          <img src="../../assets/icons/settings.svg" class="w-3 h-3" />
          <span>{{ $t('play.play_now.modes') }}</span>
        </div>
      </div>
    </div>

    <!-- Tooltip - overlay on hover, takes space when selected or on last row -->
    <div
      :class="{
        // When selected: normal flow, takes up space
        'overflow-hidden px-4 pb-2 rounded-b-lg bg-gray-900 -mt-[5px]': isCardSelected,
        // When hovering (not selected, not last row): absolute overlay
        'absolute top-full z-20 overflow-hidden px-4 pb-2 rounded-b-lg bg-gray-900 -left-px -right-px border-b border-l border-r border-gray-800 -mt-[5px]': shouldShowTooltip,
        // Visibility states
        'max-h-0 py-0 pointer-events-none': !shouldShowTooltip && !isCardSelected,
        'max-h-96 py-3 pointer-events-auto': shouldShowTooltip || isCardSelected,
      }"
    >
      <!-- Recommended for stat info -->
      <div v-if="mod.recommendedForStat" class="mb-2">
        <h3 class="text-text-primary text-sm font-medium mb-1">
          {{ mod.recommendedForStat.type === 'weakness' ? $t('play.play_now.improves_weakness') : $t('play.play_now.enhances_strength') }}
        </h3>
        <div class="flex items-center gap-2">
          <div class="flex items-center gap-1">
            <div
              class="w-2 h-2 rounded-full"
              :class="mod.recommendedForStat.type === 'weakness' ? 'bg-red-500' : 'bg-green-500'"
            />
            <p class="text-text-secondary text-xs font-medium">
              {{ toTitleCase(mod.recommendedForStat.displayName) }}
            </p>
          </div>
          <div
            v-if="mod.recommendedForStat.percentDifference"
            class="text-xs px-1.5 py-0.5 rounded"
            :class="mod.recommendedForStat.isNegative
              ? 'bg-red-900/30 text-red-300'
              : 'bg-green-900/30 text-green-300'"
          >
            {{ mod.recommendedForStat.isNegative ? '' : '+' }}{{ mod.recommendedForStat.percentDifference }}%
          </div>
        </div>
      </div>

      <!-- Description -->
      <div v-if="mod.description" class="mb-2">
        <h3 class="text-text-primary text-sm font-medium mb-1">{{ $t('play.play_now.description') }}</h3>
        <p class="text-text-secondary text-xs leading-relaxed">
          {{ selectedDescription }}
        </p>
      </div>

      <div v-if="mod?.children?.length > 0" class="mb-2">
        <h3 class="text-text-primary text-sm font-medium mb-1">{{ $t('play.play_now.modes') }}</h3>
        <div class="flex flex-wrap gap-1">
          <span
            @click.stop="selectMod(mod)"
            class="z-10 px-2 select-none py-0.5 text-text-secondary text-xs rounded-md border cursor-pointer hover:bg-primary-500 hover:text-text-primary transition-all duration-300"
            :class="{
              'border-primary-600 hover:bg-primary-600 text-primary-50 bg-primary-600 text-text-white':
                selectedMod?.id === mod?.id,
              'border-gray-700': selectedMod?.id !== mod?.id,
            }"
          >
            {{ $t('play.play_now.default_mode') }}
          </span>
          <span
            v-for="child in mod.children"
            :key="child?.id"
            @click.stop="selectMod(child)"
            class="z-10 px-2 select-none py-0.5 text-text-secondary text-xs rounded-md border cursor-pointer hover:bg-primary-500 hover:text-text-primary transition-all duration-300"
            :class="{
              'border-primary-600 hover:bg-primary-600 text-primary-50 bg-primary-600 text-text-white':
                selectedMod?.id === child?.id,
              'border-gray-700': selectedMod?.id !== child?.id,
            }"
          >
            {{ child?.friendly_name }}
          </span>
        </div>
      </div>

      <!-- Tags -->
      <div v-if="tags.length > 0" class="pb-2">
        <h3 class="text-text-primary text-sm font-medium mb-2">{{ $t('play.play_now.tags') }}</h3>
        <div class="flex flex-wrap gap-1">
          <span
            v-for="tag in tags"
            :key="tag"
            class="px-1 py-0.5 bg-gray-700 text-text-secondary text-xs rounded-md"
          >
            {{ tag }}
          </span>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch } from 'vue';
import { toTitleCase } from '../../utils/formatters.js';
import { isFavoriteMod, toggleFavoriteMod } from '../../utils/user_preferences.js';

const emit = defineEmits(['selectMod', 'hoverChange']);

const props = defineProps({
  mod: {
    type: Object,
    required: true,
  },
  category: {
    type: Object,
    required: false,
    default: null,
  },
  selectedMod: {
    type: Object,
    required: true,
  },
  showCategory: {
    type: Boolean,
    required: true,
  },
});

const showTooltip = ref(false);

const isFavorite = computed(() => isFavoriteMod(props.mod.id));

// Show tooltip only on hover when not selected
const shouldShowTooltip = computed(() => showTooltip.value && !isCardSelected.value);

// Check if this card should show as selected (parent or any child is selected)
const isCardSelected = computed(() => {
  if (!props.selectedMod) return false;

  // Check if the parent mod is selected
  if (props.selectedMod?.id === props.mod?.id) return true;

  // Check if any child mod is selected
  if (props.mod.children && Array.isArray(props.mod.children)) {
    return props.mod.children.some((child) => child?.id === props.selectedMod?.id);
  }

  return false;
});

const imageUrl = computed(() => {
  // If a child of this mod is selected, show the child's image
  if (props.selectedMod && props.mod.children && Array.isArray(props.mod.children)) {
    const selectedChild = props.mod.children.find(
      (child) => child?.id === props.selectedMod?.id,
    );
    if (selectedChild) {
      return `/img/mods/${selectedChild.name.toLowerCase()}.webp`;
    }
  }

  // Otherwise, always show this mod's image
  return `/img/mods/${props.mod?.name?.toLowerCase()}.webp`;
});

// Description - shows the selected child mod's description if a child is selected, otherwise the parent mod's description
const selectedDescription = computed(() => {
  // If a child of this mod is selected, show the child's description
  if (props.selectedMod && props.mod.children && Array.isArray(props.mod.children)) {
    const selectedChild = props.mod.children.find(
      (child) => child?.id === props.selectedMod?.id,
    );
    if (selectedChild && selectedChild.description) {
      return selectedChild?.description;
    }
  }

  // Otherwise, always show this mod's description
  return props.mod?.description;
});

// Get all tag names from the simplified tag structure
const tags = computed(() => {
  if (!props.mod.tags || !Array.isArray(props.mod.tags)) {
    return [];
  }

  return props.mod.tags.map((tag) => tag.name).filter(Boolean);
});

// Watch for hover changes and emit to parent (only for hover, not selection)
watch(showTooltip, (newValue) => {
  emit('hoverChange', newValue);
});

function toggleFavorite() {
  toggleFavoriteMod(props.mod.id);
}

function selectMod(mod) {
  emit('selectMod', mod);
}
</script>
